import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type MediaPartnerKey } from '@/api/routes/partners/partners.integration.api';
import Chip from '@/components/Chip';
import {
  INTEGRATION_SUPPORT_URL,
  formatIntegrationDate,
  getKeyState,
  maskKey,
} from '@/domain/integration/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  apiKey: MediaPartnerKey;
}

/** The key the Bodyprint team created. It is born and revoked in the admin; here it only shows. */
const IntegrationKeyCard = ({ apiKey }: Props) => {
  const t = useTranslate('domain.integration.components.IntegrationKeyCard');
  const state = getKeyState(apiKey);

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Typography variant="subtitle2" fontWeight={700}>
          {t('title')}
        </Typography>
        <Chip
          size="xsmall"
          label={t(`state.${state}`)}
          color={state === 'active' ? 'success' : 'neutral'}
          variant={state === 'active' ? 'solid' : 'outline'}
        />
      </Stack>
      <Typography variant="body2" sx={sx.key}>
        {maskKey(apiKey.last4)}
      </Typography>
      <Stack divider={<Divider />} gap={1}>
        <Row
          label={t('createdBy')}
          value={t('createdByValue', { date: formatIntegrationDate(apiKey.createdAt) })}
        />
        <Row
          label={t('lastUsed')}
          value={apiKey.lastUsedAt ? formatIntegrationDate(apiKey.lastUsedAt) : t('neverUsed')}
        />
        {apiKey.revokedAt ? (
          <Row label={t('revokedAt')} value={formatIntegrationDate(apiKey.revokedAt)} />
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t('hint')}{' '}
        <Link href={INTEGRATION_SUPPORT_URL} target="_blank" rel="noreferrer">
          {t('support')}
        </Link>
      </Typography>
    </Paper>
  );
};

export default IntegrationKeyCard;

const Row = ({ label, value }: { label: string; value: string }) => (
  <Stack gap={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    p: 2.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  key: { fontFamily: 'monospace', letterSpacing: '0.04em', overflowWrap: 'anywhere' },
};
